import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DailyCardAdminItemDto, DailyCardAdminListResponseDto, DailyCardOptionDto } from '@lunary/api-client'
import { ArchCard } from '@lunary/ui-web/arch-card'
import { resolveApiUrl } from '@lunary/ui-web/deck'
import { api } from '../../shared/api/client'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { addDays, longDate, moscowToday } from '../../shared/lib/moscow-date'
import { DayTimeline } from '../../shared/ui/day-timeline'
import type { DayInfo, DayState } from '../../shared/ui/day-timeline'
import styles from '../../shared/ui/expert.module.css'
import { CardPicker } from './card-picker'
import { DailyCardEditor } from './editor'
import { DAILY_CARDS_KEY, errorText, useChoose, useDraw } from './queries'

const MAX_DAYS_AHEAD = 7

function dayState(item: DailyCardAdminItemDto | undefined): DayState {
  if (!item) return 'empty'
  return item.status === 'published' ? 'published' : 'draft'
}

function dayInfo(item: DailyCardAdminItemDto | undefined): DayInfo {
  if (!item) return { state: 'empty', faces: [], caption: 'нет карты' }
  return { state: dayState(item), faces: [{ src: resolveApiUrl(item.face_url), name: item.card.name }], caption: item.card.name }
}

export function DailyCardPage() {
  const qc = useQueryClient()
  const today = moscowToday()
  const [date, setDate] = useState(today)
  const [picking, setPicking] = useState(false)
  const [pending, setPending] = useState<DailyCardOptionDto | null>(null)
  const [replaced, setReplaced] = useState<string | null>(null)

  const list = useQuery({
    queryKey: DAILY_CARDS_KEY,
    queryFn: () => api.get<DailyCardAdminListResponseDto>('/admin/daily-cards?limit=100'),
    retry: false,
  })

  const draw = useDraw(date)
  const choose = useChoose(date)

  const items = list.data?.items ?? []
  const byDate = new Map(items.map((item) => [item.date, item]))
  const emptyAhead = list.isSuccess
    ? Array.from({ length: MAX_DAYS_AHEAD + 1 }, (_, i) => addDays(today, i)).filter((d) => !byDate.has(d))
    : []
  const fill = useMutation({
    mutationFn: async (dates: readonly string[]) => {
      for (const d of dates) await api.post<DailyCardAdminItemDto>('/admin/daily-cards/draw', { date: d })
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: DAILY_CARDS_KEY, exact: true }),
  })
  const entry = byDate.get(date) ?? null
  const published = entry?.status === 'published'
  const past = date < today

  const apply = (option: DailyCardOptionDto) => {
    choose.mutate(option.card_id, {
      onSuccess: () => {
        setPicking(false)
        setPending(null)
        setReplaced(published ? date : null)
      },
    })
  }

  const pick = (option: DailyCardOptionDto) => {
    if (published && entry.opened_count > 0) {
      setPicking(false)
      setPending(option)
      return
    }
    apply(option)
  }

  return (
    <div>
      <header className={styles.head}>
        <div>
          <h1>Карта дня</h1>
          <p className="muted">
            Выберите карту из основной колоды для карты дня, поправьте трактовку и опубликуйте. Люди увидят её в приложении и в Telegram с 00:00 по Москве.
          </p>
        </div>
      </header>

      <DayTimeline
        today={today}
        selected={date}
        maxAhead={MAX_DAYS_AHEAD}
        label="День публикации"
        dayOf={(d) => dayInfo(byDate.get(d))}
        onSelect={setDate}
        emptyAhead={emptyAhead.length}
        filling={fill.isPending}
        onFill={() => fill.mutate(emptyAhead)}
      />
      {fill.isSuccess && <p className={styles.hint}>Пустые дни получили случайные карты с текстами по умолчанию. Это черновики: проверьте и опубликуйте.</p>}
      {fill.isError && <p className="error-box">{errorText(fill.error)}</p>}

      <section className={styles.panel} aria-labelledby="daily-card-day">
        <div className={styles.panelHead}>
          <h2 id="daily-card-day">{longDate(date)}</h2>
          {entry && (
            <span className={published ? styles.statusPublished : styles.statusDraft}>
              {published ? 'опубликовано' : 'черновик'}
            </span>
          )}
          <span className={styles.spacer} />
        </div>

        {list.isPending && <div className="spinner" aria-label="Загрузка" />}
        {list.isError && <p className="error-box">Не удалось загрузить карты дня.</p>}
        {(draw.error ?? choose.error) && <p className="error-box">{errorText(draw.error ?? choose.error)}</p>}

        {entry && (
          <DailyCardEditor
            key={`${entry.id}:${entry.status}:${entry.card.id}`}
            entry={entry}
            readOnly={past}
            replaced={replaced === entry.date}
            onPick={() => setPicking(true)}
            onDraw={() => draw.mutate()}
            drawing={draw.isPending}
          />
        )}
        {!entry && !list.isPending && past && (
          <div className={styles.empty}>
            <p>В этот день карта дня не публиковалась.</p>
          </div>
        )}
        {!entry && !list.isPending && !past && (
          <div className={styles.empty}>
            <span className={styles.arch}>
              <ArchCard number={0} name="Карта не вытянута" size="md" />
            </span>
            <p>На этот день карта ещё не выбрана. Выберите её из колоды для карты дня: 22 старших и свои карты.</p>
            <div className={styles.actions}>
              <button type="button" onClick={() => setPicking(true)}>
                Выбрать карту
              </button>
              <button type="button" className="secondary" disabled={draw.isPending} onClick={() => draw.mutate()}>
                {draw.isPending ? 'Тяну…' : 'Случайная из колоды'}
              </button>
            </div>
          </div>
        )}
      </section>

      <CardPicker
        key={`${date}:${entry?.card.id ?? ''}:${picking}`}
        open={picking}
        current={entry?.card.id ?? null}
        pending={choose.isPending}
        onClose={() => setPicking(false)}
        onPick={pick}
      />
      <ConfirmDialog
        open={pending !== null}
        title="Заменить опубликованную карту?"
        text={`Карту уже открыли: ${entry?.opened_count ?? 0}. Они увидят «${pending?.name ?? ''}» вместо «${entry?.card.name ?? ''}», текст заменится описанием по умолчанию новой карты.`}
        confirmLabel="Заменить"
        danger
        pending={choose.isPending}
        error={choose.isError ? errorText(choose.error) : null}
        onClose={() => setPending(null)}
        onConfirm={() => pending && apply(pending)}
      />
    </div>
  )
}
